import { waitForNextPaint } from '@packages/utils/dom.ts'

import events from '@layers/web/constants/events.js'
import localStorageKeys from '@layers/web/constants/local-storage-keys.ts'
import { sha256 } from '@layers/web/utils/hash.ts'
import { IDENTIFIED_USER_MAX_AGE_MS, META_HASHED_PHONE_PARAMETER } from '@layers/web/constants/tracking.ts'
import type {
  Rbcs,
  UserConsentStatus,
  HashedPhone,
  HashedIdentity,
  StoredIdentifiedUser,
} from '@layers/web/utils/types/tracking.ts'
import { DYNAMIC_AIRPORT } from '@layers/web/constants/airports.ts'
import type { TripInfo } from '@layers/web/stores/types/book.ts'
import type { Trip } from '@layers/web/stores/types/trip.ts'
import type { GoogleAnalyticsItem } from '@layers/web/services/types/booking-api.ts'
import type { FavouriteEventParams, PhoneNumber, ProductEventParams, PurchaseItem } from '@layers/web/composables/types/tracking.ts'

function pushDataLayer (obj: Record<string, any>) {
  if (import.meta.server) {
    return
  }

  if (!window.dataLayer) {
    window.dataLayer = []
  }

  window.dataLayer.push(obj)
}

const transformRbcsToUserConsentStatus = (rbcs: Rbcs): UserConsentStatus => ({
  all: !!(rbcs.all ?? (rbcs.f && rbcs.a && rbcs.p)),
  any: !!(rbcs.all || rbcs.f || rbcs.a || rbcs.p),
  advertising: !!(rbcs.a || rbcs.all),
  performance: !!(rbcs.p || rbcs.all),
  functional: !!(rbcs.f || rbcs.all),
  cookieVersion: rbcs.v ?? undefined,
  timestamp: rbcs.t ?? undefined,
})

const waitForConsentUpdate = () => {
  const hasAnsweredConsent = checkHasAnsweredConsent()
  if (!hasAnsweredConsent) {
    return new Promise<void>((resolve) => {
      window.addEventListener(
        events.rbcsChange,
        () => resolve()
      )
    })
  }
}

const trackAfterScriptLoad = <ReturnType>(
  ...args: [
    () => boolean,
    () => ReturnType,
  ]
) => {
  return new Promise<ReturnType | void>(async (resolve) => {
    const [hasLoadedEvaluation, func] = args

    await waitForConsentUpdate()

    let hasLoaded
    try {
      await promiseUntilCondition(
        hasLoadedEvaluation,
        {
          interval: 200,
          retries: 150,
        }
      )
      hasLoaded = true
    } catch (e) {
      // Script might not load due to declined cookie consent (expected)
      hasLoaded = false
    }

    if (hasLoaded) {
      resolve(await func())
      return
    }

    resolve()
  })
}

const defaultUserConsentStatus: UserConsentStatus = {
  all: false,
  any: false,
  advertising: false,
  performance: false,
  functional: false,
}

const getUserConsentStatus = (): UserConsentStatus => import.meta.server
  ? defaultUserConsentStatus
  : (window.userConsent || defaultUserConsentStatus)

const checkHasAnsweredConsent = () => {
  return !!window.userConsent
}

/** Identity may only be stored for the tracking purposes the visitor has consented to */
const hasTrackingConsent = (userConsent: UserConsentStatus) => userConsent.advertising || userConsent.performance

const hashEmail = (email: string) => sha256(email.trim().toLowerCase())

const hashPhone = async ({ countryCode, number }: PhoneNumber): Promise<HashedPhone> => {
  const digits = toInternationalPhoneDigits(countryCode, number)

  return {
    phoneHashed: await sha256(digits),
    phoneE164Hashed: await sha256(`+${digits}`),
  }
}

const clearIdentifiedUserHash = () => tryRemoveLocalStorage(localStorageKeys.identifiedUser)

const getStoredIdentifiedUser = (): HashedIdentity | undefined => {
  const stored = tryGetLocalStorage(localStorageKeys.identifiedUser)
  if (!stored) {
    return
  }

  try {
    const { emailHashed, phoneHashed, phoneE164Hashed, identifiedAt }: StoredIdentifiedUser = JSON.parse(stored)
    if (Date.now() - identifiedAt < IDENTIFIED_USER_MAX_AGE_MS) {
      return { emailHashed, phoneHashed, phoneE164Hashed }
    }
  } catch (e) {
    // Unreadable value from an older format - treated as not identified below
  }

  clearIdentifiedUserHash()
}

/**
 * Adds the identity to the one stored for the visitor and returns the combined identity. Another email
 * than the stored one starts a new identity, so a phone number stored for one person never travels with
 * the email of the next person using the same browser.
 */
const storeIdentifiedUser = (identity: HashedIdentity): HashedIdentity => {
  const storedIdentity = getStoredIdentifiedUser()
  const isAnotherPerson = !!identity.emailHashed && !!storedIdentity?.emailHashed && identity.emailHashed !== storedIdentity.emailHashed

  const combinedIdentity: HashedIdentity = isAnotherPerson
    ? identity
    : {
      emailHashed: identity.emailHashed ?? storedIdentity?.emailHashed,
      phoneHashed: identity.phoneHashed ?? storedIdentity?.phoneHashed,
      phoneE164Hashed: identity.phoneE164Hashed ?? storedIdentity?.phoneE164Hashed,
    }

  if (hasTrackingConsent(getUserConsentStatus())) {
    const storedIdentifiedUser: StoredIdentifiedUser = {
      ...combinedIdentity,
      identifiedAt: Date.now(),
    }

    trySetLocalStorage(localStorageKeys.identifiedUser, JSON.stringify(storedIdentifiedUser))
  }

  return combinedIdentity
}

/**
 * Used for deduplication in conversion tracking where the id of the pixel event has to be the same as the gtm server-side tag event
 */
const generateTrackingEventId = () => `${Date.now()}.${generateId()}`

/**
 * Helper function to normalize categories for different platforms
 */
const normalizeCategories = (categories: string | string[] | undefined): {
    googleAnalytics: Record<string, string>
    joined: string | undefined
  } => {
  if (!categories || !categories.length) {
    return { googleAnalytics: {}, joined: undefined }
  }

  if (Array.isArray(categories)) {
    const sortedLowerCaseCategories = categories
      .map((category) => category.toLowerCase())
      .sort((a, b) => a.localeCompare(b))

    const gaCategories: Record<string, string> = {}
    if (sortedLowerCaseCategories[0]) {
      gaCategories.item_category = sortedLowerCaseCategories[0]
    }
    if (sortedLowerCaseCategories[1]) {
      gaCategories.item_category2 = sortedLowerCaseCategories[1]
    }
    if (sortedLowerCaseCategories[2]) {
      gaCategories.item_category3 = sortedLowerCaseCategories[2]
    }
    if (sortedLowerCaseCategories[3]) {
      gaCategories.item_category4 = sortedLowerCaseCategories[3]
    }
    if (sortedLowerCaseCategories[4]) {
      gaCategories.item_category5 = sortedLowerCaseCategories[4]
    }

    return {
      googleAnalytics: gaCategories,
      joined: sortedLowerCaseCategories.join(','), // comma separated for Facebook/TikTok
    }
  }

  const lowerCaseCategory = categories?.toLowerCase?.()

  return {
    googleAnalytics: {
      item_category: lowerCaseCategory,
    },
    joined: lowerCaseCategory,
  }
}

/**
 * Catalogue id for a promoted, specific departure (product id `DEPARTUREID-AIRPORT`,
 * sharing the trip type's group id). Returns null for trip-type-only bookings
 *
 * Sent alongside the regular `getTrackingContent` id so non-promoted departures
 * still match the trip-per-airport catalogue item and matching % stays high.
 */
const getDepartureCatalogueItemId = (tripDepartureId: number, airport?: string): string => {
  return `${tripDepartureId}-${airport || 'BUSS'}`
}

const getTrackingContent = (
  tripId: string | number,
  airport?: string,
  isDeparturePromoted?: boolean,
  tripDepartureId?: number
): {
  contentType: 'product' | 'product_group'
  contentId: string
} => {
  if (isDeparturePromoted && tripDepartureId) {
    return {
      contentType: 'product',
      contentId: getDepartureCatalogueItemId(tripDepartureId),
    }
  }

  if (!airport || airport === DYNAMIC_AIRPORT) {
    // No airport selected or dynamic trip - use product_group
    return {
      contentType: 'product_group',
      contentId: String(tripId), // Base trip ID (e.g., "14")
    }
  }

  // Airport selected - use product
  return {
    contentType: 'product',
    contentId: `${tripId}-${airport}`, // Combined ID (e.g., "14-STO")
  }
}

/**
 * @see https://developers.google.com/tag-platform/gtagjs/reference
 */
function gtag (..._args: any[]) {
  if (import.meta.server) {
    return
  }

  if (!window.dataLayer) {
    window.dataLayer = []
  }

  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

const pageTtq = (
  ...args: Parameters<NonNullable<Window['ttq']>['page']>
) => trackAfterScriptLoad(
  () => !!window.ttq?.page,
  () => window.ttq!.page(...args)
)

const pageCio = (
  ...args: Parameters<NonNullable<Window['cioanalytics']>['page']>
) => trackAfterScriptLoad(
  () => !!window.cioanalytics?.page,
  () => window.cioanalytics!.page(...args)
)

const identifyCio = (
  ...args: Parameters<NonNullable<Window['cioanalytics']>['identify']>
) => trackAfterScriptLoad(
  () => !!window.cioanalytics?.identify,
  () => window.cioanalytics!.identify(...args)
)

const resetCio = () => trackAfterScriptLoad(
  () => !!window.cioanalytics?.reset,
  () => window.cioanalytics!.reset?.()
)

const trackGtag = async (
  ...args: any
) => {
  if (import.meta.server) {
    return
  }

  await waitForConsentUpdate()
  await waitForNextPaint()
  gtag(...args)
}

const trackCio = (
  ...args: Parameters<NonNullable<Window['cioanalytics']>['track']>
) => trackAfterScriptLoad(
  () => !!window.cioanalytics?.track,
  () => window.cioanalytics!.track(...args)
)

/**
 * Associates our internal (non-PII) user id with the gtag session, so every
 * event tracked afterwards is attributed to that user in GA4 reporting. The same
 * hash also goes out as user-provided data on every hit, which is what the
 * server-side GTM container forwards to Meta's Conversions API as the email
 * match key - the browser pixel gets it separately, see identifyFbqUser.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/user-id
 * @see https://developers.google.com/tag-platform/gtagjs/reference#set
 */
const identifyGtagUser = (emailHashed: string) => {
  trackGtag('set', 'user_id', emailHashed)
  trackGtag('set', 'user_data', { sha256_email_address: emailHashed })
}

/**
 * Sends the phone hash with every following GA4 hit, for the server-side GTM container to pass on to
 * Meta's Conversions API, see META_HASHED_PHONE_PARAMETER
 */
const identifyGtagPhone = (phoneHashed: string) => {
  trackGtag('set', { [META_HASHED_PHONE_PARAMETER]: phoneHashed })
}

const trackFbq = (
  ...args: Parameters<NonNullable<Window['fbq']>>
) => trackAfterScriptLoad(
  () => !!window.fbqInitialized,
  async () => {
    /**
     * Timeout on tracking events to fix the race condition of consent mode vs tracking events (consent mode first)
     * 1. User enters website and accepts consent
     * 2. Pixel starts loading while trackFbq is called for both consent and also any eventual events
     * 3. Pixel finishes loading and this function will execute, and no one knows if consent or tracking events fire first¨
     * Consent is denied by default so if the tracking event fires first then it will have less data
     */
    if (['track', 'trackCustom'].includes(args[0])) {
      await waitMs(400)
    }

    return window.fbq!(...args)
  }
)

let identifiedFbqUserData: string | undefined

/**
 * Attaches the hashed email and phone number to the Meta pixel as Advanced Matching
 * data, so every pixel event fired afterwards carries them. Meta only documents passing
 * the data in the initial fbq('init'), which GTM fires long before the visitor is known,
 * but fbevents.js accepts a second init for the same pixel id: a pixel without user
 * data adopts the given data instead of warning about a duplicate. A pixel that
 * already has user data ignores later inits, hence the dedupe on the data, so a phone
 * number given after the email reaches the pixel from the next page load, through the
 * stored identity.
 * @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
 */
const identifyFbqUser = (pixelId: string, { emailHashed, phoneHashed }: HashedIdentity) => trackAfterScriptLoad(
  () => !!window.fbqInitialized,
  () => {
    const userData = {
      ...(emailHashed && { em: emailHashed }),
      ...(phoneHashed && { ph: phoneHashed }),
    }
    const userDataKey = JSON.stringify(userData)

    if (identifiedFbqUserData === userDataKey) {
      return
    }

    identifiedFbqUserData = userDataKey

    return window.fbq!('init', pixelId, userData)
  }
)

const trackTtq = (
  ...args: Parameters<NonNullable<Window['ttq']>['track']>
) => trackAfterScriptLoad(
  () => !!window.ttq,
  () => window.ttq!.track(...args)
)

/**
 * Attaches the hashed email and phone number to the TikTok pixel as Advanced Matching
 * data, so every pixel event fired afterwards carries them. The pixel passes a SHA-256
 * hash on as it is and adds the data of a later identify to the earlier one.
 * @see https://ads.tiktok.com/help/article/advanced-matching-web
 */
const identifyTtqUser = ({ emailHashed, phoneE164Hashed }: HashedIdentity) => {
  if (!emailHashed && !phoneE164Hashed) {
    return
  }

  return trackAfterScriptLoad(
    () => !!window.ttq,
    () => window.ttq!.identify({
      ...(emailHashed && { email: emailHashed }),
      ...(phoneE164Hashed && { phone_number: phoneE164Hashed }),
    })
  )
}

const trackClarity = (
  ...args: Parameters<NonNullable<Window['clarity']>>
) => trackAfterScriptLoad(
  () => typeof window.clarity === 'function',
  () => window.clarity!(...args)
)

const trackUetq = (
  ...args: Parameters<any>
) => {
  if (!window.uetq) {
    window.uetq = []
  }

  return window.uetq.push(...args)
}

const trackNavigationAreaClick = (section: string, linkName?: string, linkUrl?: string): void => {
  trackGtag('event', `${section}_click`, {
    link_name: linkName,
    link_url: linkUrl,
    section,
    page_location: window.location.href,
  })
}

/**
 * The tracking departure airport for a booking. Prefers the selected flight
 * offer's departure IATA; falls back to the trip's own airport (bus trips have
 * no flight offer). Mirrors the book page's former `getAirportString`.
 */
const tripInfoToTrackingAirport = (tripinfo: TripInfo): string | undefined => {
  const flightAirport = tripinfo.flightOffer?.[0]?.itinieraries?.[0]?.segments?.[0]?.departure?.iataCode
  if (flightAirport) {
    return flightAirport
  }

  const tripAirport = tripinfo.airport
  if (!tripAirport) {
    return
  }

  return typeof tripAirport === 'string'
    ? tripAirport
    : tripAirport.iata
}

/**
 * Maps a booking's `tripinfo` to the product fields shared by the add_to_cart,
 * begin_checkout and purchase events. Replaces the per-call-site builders.
 */
const tripInfoToProductParams = (tripinfo: TripInfo): ProductEventParams => ({
  name: tripinfo.trip?.title || tripinfo.tripname || '',
  id: tripinfo.triptypeid || tripinfo.server_tripid || tripinfo.tripid || '',
  value: tripinfo.priceSek || 0,
  categories: (tripinfo.trip?.tags ?? []).map(({ rubrik }) => rubrik),
  departureDate: tripinfo.tripdate,
  returnDate: tripinfo.returndate,
  airport: tripInfoToTrackingAirport(tripinfo),
  tripDepartureId: tripinfo.tripDepartureId,
  isDeparturePromoted: tripinfo.isDeparturePromoted,
  trip: tripinfo.trip,
})

/**
 * Favourite event fields taken from the trip a card already renders, so call sites
 * only have to hand the favourite icon its trip. The id stays with the caller since
 * the favourite can be stored under another id than the trip's own.
 */
const tripToFavouriteEventParams = (trip: Trip): Omit<FavouriteEventParams, 'id'> => ({
  name: trip.title,
  value: trip.price_min,
  categories: (trip.tags ?? []).map(({ rubrik }) => rubrik),
  trip,
})

/**
 * Purchase line items: keep the confirmation-API item shape but override id/name
 * with the booked trip's values when available. Replaces `toPurchaseItems`.
 */
const tripInfoToPurchaseItems = (
  tripinfo: TripInfo,
  items: GoogleAnalyticsItem[]
): PurchaseItem[] => items.map((item) => ({
  item_id: String(tripinfo.trip?.id ?? item.id),
  item_name: tripinfo.trip?.title ?? item.name,
  item_category: item.category,
}))

export {
  pushDataLayer,
  transformRbcsToUserConsentStatus,
  gtag,
  trackAfterScriptLoad,
  generateTrackingEventId,
  pageTtq,
  pageCio,
  identifyCio,
  identifyGtagUser,
  identifyGtagPhone,
  identifyFbqUser,
  identifyTtqUser,
  hasTrackingConsent,
  hashEmail,
  hashPhone,
  storeIdentifiedUser,
  getStoredIdentifiedUser,
  clearIdentifiedUserHash,
  trackCio,
  trackFbq,
  trackTtq,
  trackUetq,
  resetCio,
  trackGtag,
  trackClarity,
  getUserConsentStatus,
  waitForConsentUpdate,
  normalizeCategories,
  getTrackingContent,
  getDepartureCatalogueItemId,
  trackNavigationAreaClick,
  tripInfoToTrackingAirport,
  tripInfoToProductParams,
  tripToFavouriteEventParams,
  tripInfoToPurchaseItems,
}
